/**
 * API Client Service
 *
 * Centralized axios instance with authentication interceptors.
 * This is the SINGLE source for all API requests in the application.
 *
 * Features:
 * - Automatic auth token injection
 * - Centralized 401 handling (session expiry)
 * - Consistent error handling
 * - Request/Response logging in development
 *
 * Usage:
 *   import { apiClient } from '@/services/apiClient';
 *   const response = await apiClient.get('/exam');
 */

import axios from 'axios';
import { API_BASE_URL, API_TIMEOUT, ENABLE_DEBUG_LOGGING } from '../config/environment';
import { storageService } from './storageService';
import { navigateTo } from './navigationService';

// Create the axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - adds auth token to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = storageService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (ENABLE_DEBUG_LOGGING) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    if (ENABLE_DEBUG_LOGGING) {
      console.error('[API Request Error]', error);
    }
    return Promise.reject(error);
  }
);

// Response interceptor - handles auth errors and logging
apiClient.interceptors.response.use(
  (response) => {
    if (ENABLE_DEBUG_LOGGING) {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    }
    return response;
  },
  (error) => {
    if (ENABLE_DEBUG_LOGGING) {
      console.error('[API Response Error]', error.response?.status, error.message);
    }

    // Handle 401 Unauthorized - session expired
    if (error.response?.status === 401) {
      storageService.clearAuth();

      // Show a user-friendly message
      const errorMessage = error.response?.data?.message || 'Your session has expired. Please login again.';

      // Store the error message in sessionStorage to display on login page
      sessionStorage.setItem('authError', errorMessage);

      navigateTo('/login');
    }

    return Promise.reject(error);
  }
);

/**
 * Helper to extract error message from API error response
 */
export const getErrorMessage = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

/**
 * Create a resource-specific API client
 * Useful for creating typed API modules
 *
 * @param {string} resourcePath - The resource path (e.g., '/exam', '/reading')
 * @returns {Object} - CRUD methods for the resource
 */
export const createResourceApi = (resourcePath) => ({
  getAll: (params) => apiClient.get(resourcePath, { params }),
  getById: (id) => apiClient.get(`${resourcePath}/${id}`),
  create: (data) => apiClient.post(resourcePath, data),
  update: (id, data) => apiClient.put(`${resourcePath}/${id}`, data),
  delete: (id) => apiClient.delete(`${resourcePath}/${id}`),
});

export { apiClient };
export default apiClient;
